/** Screen 4+5: the actual quiz round and its result. */
import { el, page } from '../dom';
import { navigate } from '../router';
import { getSet, updateCards } from '../../core/storage';
import { applyGrade, buildQuestion, pickCard, resolveDirection } from '../../core/leitner';
import { cardsOfGroup } from '../../core/groups';
import { gradeAnswer } from '../../core/answer';
import { canSpeak, speak } from '../../core/tts';
import type {
  AnswerGrade,
  Card,
  Question,
  RoundMistake,
  RoundSettings,
  VocabSet,
} from '../../core/types';

type PendingRound = {
  setId: string;
  settings: RoundSettings;
  /** When set, the round only uses these cards (used for "Fehler üben"). */
  cardIds: string[] | null;
};

let pending: PendingRound | null = null;

export function startRound(
  setId: string,
  settings: RoundSettings,
  cardIds: string[] | null,
): void {
  pending = { setId, settings, cardIds };
  navigate(`/quiz/${setId}`);
}

type RoundState = {
  set: VocabSet;
  settings: RoundSettings;
  pool: Card[];
  remaining: Card[];
  total: number;
  index: number;
  correct: number;
  mistakes: RoundMistake[];
  lastId: string | null;
  current: Question | null;
};

function languageOf(set: VocabSet, question: Question): string {
  return question.direction === 'native2foreign' ? set.foreignLang : set.nativeLang;
}

function promptLanguageOf(set: VocabSet, question: Question): string {
  return question.direction === 'native2foreign' ? set.nativeLang : set.foreignLang;
}

function speakerButton(text: string, languageCode: string): HTMLElement | null {
  if (!canSpeak(languageCode)) return null;
  return el('button', {
    class: 'btn btn-ghost btn-speak',
    type: 'button',
    'aria-label': 'Wort vorlesen',
    text: '🔊',
    onclick: () => speak(text, languageCode),
  });
}

export function quizScreen(context: { params: string[] }): HTMLElement {
  const setId = context.params[0];
  const set = getSet(setId);

  if (!set || !pending || pending.setId !== setId) {
    // Happens after a page reload - there is no round to continue.
    navigate(`/learn/${setId}`, true);
    return el('div');
  }

  const settings = pending.settings;
  const allowed = pending.cardIds;
  const pool = allowed
    ? set.cards.filter((card) => allowed.includes(card.id))
    : cardsOfGroup(set, settings.group);

  if (pool.length === 0) {
    navigate(`/learn/${setId}`, true);
    return el('div');
  }

  const state: RoundState = {
    set,
    settings,
    pool,
    remaining: pool.slice(),
    total: Math.min(settings.length, pool.length),
    index: 0,
    correct: 0,
    mistakes: [],
    lastId: null,
    current: null,
  };

  const container = el('div', { class: 'quiz-root' });

  function nextQuestion(): void {
    if (state.index >= state.total || state.remaining.length === 0) {
      container.replaceChildren(resultView(state));
      return;
    }

    const card = pickCard(state.remaining, state.lastId);
    if (!card) {
      container.replaceChildren(resultView(state));
      return;
    }

    state.remaining = state.remaining.filter((entry) => entry.id !== card.id);
    state.lastId = card.id;

    const direction = resolveDirection(state.settings.direction);
    state.current = buildQuestion(
      card,
      direction,
      // Distractors come from the whole set: a learning goal with four words
      // would otherwise offer no real choice.
      state.set.cards,
      state.settings.mode === 'choice',
    );

    container.replaceChildren(questionView(state, submitAnswer));
  }

  function submitAnswer(grade: AnswerGrade, given: string): void {
    const question = state.current;
    if (!question) return;

    const updated = applyGrade(question.card, grade);
    updateCards(state.set.id, [updated]);
    state.pool = state.pool.map((card) => (card.id === updated.id ? updated : card));

    state.index += 1;
    if (grade === 'correct') {
      state.correct += 1;
    } else if (grade === 'wrong') {
      state.mistakes.push({
        cardId: question.card.id,
        prompt: question.prompt,
        answer: question.answer,
        given,
      });
    }

    container.replaceChildren(
      feedbackView(state, question, grade, given, () => nextQuestion()),
    );
  }

  nextQuestion();

  return page({
    title: `${state.set.name}`,
    back: `/learn/${state.set.id}`,
    children: [container],
  });
}

function roundProgress(state: RoundState): HTMLElement {
  const percent = Math.round((state.index / state.total) * 100);
  return el('div', { class: 'round-progress' }, [
    el('div', { class: 'progress' }, [
      el('div', { class: 'progress-fill', style: `width:${percent}%` }),
    ]),
    el('p', {
      class: 'round-counter',
      text: `Wort ${Math.min(state.index + 1, state.total)} von ${state.total} · ${state.correct} richtig`,
    }),
  ]);
}

function questionView(state: RoundState, submit: (grade: AnswerGrade, given: string) => void): HTMLElement {
  const question = state.current;
  if (!question) return el('div');

  const promptLang = promptLanguageOf(state.set, question);
  const answerLang = languageOf(state.set, question);

  const promptBlock = el('div', { class: 'card prompt-card' }, [
    el('p', { class: 'prompt-lang', text: `${promptLang} → ${answerLang}` }),
    el('div', { class: 'prompt-row' }, [
      el('p', { class: 'prompt-word', text: question.prompt }),
      speakerButton(question.prompt, promptLang),
    ]),
  ]);

  if (state.settings.mode === 'choice' && question.options) {
    const options = question.options.map((option) =>
      el('button', {
        class: 'btn btn-option',
        type: 'button',
        text: option,
        onclick: () => submit(gradeAnswer(option, question.answer), option),
      }),
    );

    return el('div', { class: 'quiz-view' }, [roundProgress(state), promptBlock, el('div', { class: 'options' }, options)]);
  }

  if (state.settings.mode === 'typing') {
    const input = el('input', {
      class: 'input input-answer',
      type: 'text',
      autocapitalize: 'off',
      autocorrect: 'off',
      autocomplete: 'off',
      spellcheck: 'false',
      enterkeyhint: 'go',
      placeholder: `Wort auf ${answerLang}`,
      'aria-label': `Antwort auf ${answerLang}`,
    }) as HTMLInputElement;

    const send = (): void => submit(gradeAnswer(input.value, question.answer), input.value.trim());

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        send();
      }
    });

    const form = el('div', { class: 'card' }, [
      input,
      el('div', { class: 'row' }, [
        el('button', { class: 'btn btn-primary btn-big', type: 'button', text: 'Prüfen', onclick: send }),
        el('button', {
          class: 'btn btn-ghost',
          type: 'button',
          text: 'Weiss ich nicht',
          onclick: () => submit('wrong', input.value.trim()),
        }),
      ]),
    ]);

    // Autofocus once the element is in the document.
    queueMicrotask(() => input.focus());

    return el('div', { class: 'quiz-view' }, [roundProgress(state), promptBlock, form]);
  }

  // Flashcard: show the answer first, then let the learner grade themselves.
  const reveal = el('div', { class: 'card flashcard' }, [
    el('button', {
      class: 'btn btn-primary btn-big',
      type: 'button',
      text: 'Umdrehen',
      onclick: () => {
        reveal.replaceChildren(
          el('div', { class: 'prompt-row' }, [
            el('p', { class: 'prompt-word answer-word', text: question.answer }),
            speakerButton(question.answer, answerLang),
          ]),
          el('div', { class: 'row' }, [
            el('button', {
              class: 'btn btn-success btn-big',
              type: 'button',
              text: '✓ Gewusst',
              onclick: () => submit('correct', question.answer),
            }),
            el('button', {
              class: 'btn btn-danger btn-big',
              type: 'button',
              text: '✗ Nicht gewusst',
              onclick: () => submit('wrong', ''),
            }),
          ]),
        );
      },
    }),
  ]);

  return el('div', { class: 'quiz-view' }, [roundProgress(state), promptBlock, reveal]);
}

function feedbackView(
  state: RoundState,
  question: Question,
  grade: AnswerGrade,
  given: string,
  next: () => void,
): HTMLElement {
  const answerLang = languageOf(state.set, question);

  const headline =
    grade === 'correct' ? 'Richtig!' : grade === 'near' ? 'Fast richtig!' : 'Leider falsch';

  const button = el('button', {
    class: 'btn btn-primary btn-big',
    type: 'button',
    text: state.index >= state.total ? 'Ergebnis ansehen' : 'Weiter',
    onclick: next,
  }) as HTMLButtonElement;

  queueMicrotask(() => button.focus());

  return el('div', { class: 'quiz-view' }, [
    roundProgress(state),
    el('div', { class: `card feedback feedback-${grade}` }, [
      el('p', { class: 'feedback-title', text: headline }),
      el('p', { class: 'feedback-prompt', text: question.prompt }),
      el('div', { class: 'prompt-row' }, [
        el('p', { class: 'feedback-answer', text: question.answer }),
        speakerButton(question.answer, answerLang),
      ]),
      grade !== 'correct' && given
        ? el('p', {
            class: 'feedback-given',
            text:
              state.settings.mode === 'choice'
                ? `Du hast gewählt: ${given}`
                : `Du hast geschrieben: ${given}`,
          })
        : null,
    ]),
    el('div', { class: 'bottom-actions' }, [button]),
  ]);
}

function resultView(state: RoundState): HTMLElement {
  const answered = state.index;
  const percent = answered === 0 ? 0 : Math.round((state.correct / answered) * 100);

  const praise =
    percent >= 90
      ? 'Super gemacht! 🎉'
      : percent >= 70
        ? 'Gut gemacht! 👍'
        : percent >= 50
          ? 'Weiter so - Übung macht den Meister.'
          : 'Nicht aufgeben, du schaffst das!';

  const mistakeList =
    state.mistakes.length > 0
      ? el('div', { class: 'card' }, [
          el('h2', { class: 'section-title', text: 'Diese Wörter waren schwierig' }),
          el(
            'ul',
            { class: 'mistake-list' },
            state.mistakes.map((mistake) =>
              el('li', { class: 'mistake' }, [
                el('span', { class: 'mistake-prompt', text: mistake.prompt }),
                el('span', { class: 'preview-arrow', text: '→' }),
                el('span', { class: 'mistake-answer', text: mistake.answer }),
              ]),
            ),
          ),
        ])
      : null;

  const mistakeIds = Array.from(new Set(state.mistakes.map((mistake) => mistake.cardId)));

  return el('div', { class: 'quiz-view' }, [
    el('div', { class: 'card result' }, [
      el('p', { class: 'result-score', text: `${state.correct} von ${answered} richtig` }),
      el('p', { class: 'result-percent', text: `${percent}%` }),
      el('p', { class: 'result-praise', text: praise }),
    ]),
    mistakeList,
    el('div', { class: 'bottom-actions' }, [
      mistakeIds.length > 0
        ? el('button', {
            class: 'btn btn-primary btn-big',
            type: 'button',
            text: 'Fehler üben',
            onclick: () =>
              startRound(
                state.set.id,
                { ...state.settings, length: Math.max(mistakeIds.length, 1) },
                mistakeIds,
              ),
          })
        : null,
      el('button', {
        class: 'btn btn-primary btn-big',
        type: 'button',
        text: 'Nochmals üben',
        onclick: () => startRound(state.set.id, state.settings, null),
      }),
      el('button', {
        class: 'btn btn-ghost btn-big',
        type: 'button',
        text: 'Fertig',
        onclick: () => navigate('/sets'),
      }),
    ]),
  ]);
}
